<script lang="ts">
	import { clone } from "@bufbuild/protobuf";
	import { Alert, Helper } from "flowbite-svelte-v1";
	import { getContext, onMount } from "svelte";
	import { twMerge } from "tailwind-merge";
	import { PageSchema } from "#gen/pages/v2/page_pb.js";
	import P from "#lib/components/P.svelte";
	import Toggle from "#lib/components/Toggle.svelte";
	import PlaintextEditor from "#lib/components/content-editors/PlaintextEditor.svelte";
	import type { ComponentEditingProps } from "#lib/components/page/ComponentProps.js";
	import EditorInset from "#lib/components/page/editing/EditorInset.svelte";
	import { isFeatureEnabled } from "#lib/featureFlags.js";
	import type { JwtPayload } from "#lib/jwt.js";
	import FeatureFlagGate from "#lib/permissions/FeatureFlagGate.svelte";
	import type { JsonObject } from "#lib/protobuf-compat.js";
	import { sentenceCase } from "#lib/string.js";
	import { booleanValue, setBooleanValue, setStringValue, setStructValue, stringValue, structValue } from "#lib/struct.js";
	import type { TokenProvider } from "#lib/tokenProvider.js";
	import type { Client } from "@optimizely/optimizely-sdk";
	import type { Writable } from "svelte/store";

	interface VideoEmbedProps extends ComponentEditingProps {
		errors?: { config?: Record<string, { src?: string }> };
	}

	const provider: Writable<TokenProvider> = getContext("tokenProvider");
	const flags: Client = getContext("flags");
	const claims: Writable<JwtPayload> = getContext("claims");

	let { id, page, errors, onChange }: VideoEmbedProps = $props();

	let error = $derived(errors?.config?.[id]?.src);
	let initiallyUnset = $state(false);

	const src = $derived(stringValue(structValue($page.config, id), "src"));
	const autoPlay = $derived(Boolean(booleanValue(structValue($page.config, id), "autoplay")));
	const className = $derived(stringValue(structValue($page.config, id), "class"));
	const kalturaEnabled = $derived(isFeatureEnabled(flags, "kaltura_embedded_videos", $claims));
	const supportedProviders: string[] = $derived(["Youtube", "Vimeo", kalturaEnabled ? "Kaltura" : undefined, "Synthesia", "Wistia"].filter(Boolean) as string[]);
	const formatter = new Intl.ListFormat("en", {
		style: "long",
		type: "conjunction",
	});
	const supportedProvidersString = $derived(formatter.format(supportedProviders));

	onMount(() => {
		initiallyUnset = stringValue(structValue($page.config, id), "src") === undefined;
	});

	export function validate(): boolean {
		return true;
	}

	function update(e: CustomEvent<string | undefined>) {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		if (!e.detail && initiallyUnset) {
			delete config.src;
		} else {
			setStringValue(config, "src", e.detail ?? "");
		}
		$page = clone(PageSchema, $page);
	}

	function handleChange(e: CustomEvent<string | undefined>) {
		onChange?.(e.detail);
	}

	function toggleAutoplay(e: Event) {
		let config = structValue($page.config, id);
		if (!config) {
			config = {} as JsonObject;
			setStructValue($page.config, id, config);
		}
		setBooleanValue(config, "autoplay", (e.currentTarget as HTMLInputElement).checked);
		$page = $page;
		onChange?.();
	}
</script>

{#snippet children()}
	<PlaintextEditor color={error ? "red" : "base"} provider={$provider} value={src} on:input={update} on:change={handleChange} aria-labelledby="{id}-label" />
	{#if error}
		<Helper color="red" class="text-red-700">{sentenceCase(error)}</Helper>
	{/if}
	<Toggle size="small" checked={autoPlay} on:change={toggleAutoplay}>
		<P size="sm" weight="medium">Autoplay</P>
	</Toggle>
{/snippet}

<EditorInset>
	<FeatureFlagGate flag="build_your_own_page">
		<div class="flex flex-col gap-2 px-6">
			<div class="flex w-full flex-col gap-5 rounded-lg border p-3">
				<P class="text-sm font-bold text-black"><FeatureFlagGate flag="editor_toolbar_revamp_q4_2026">Content{#snippet fallback()}Attributes{/snippet}</FeatureFlagGate></P>
				<div class="flex flex-col gap-2">
					<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Source</P>
					<div class="flex flex-col gap-2">
						<div class="flex flex-col border-l-[5px] border-l-gray-300 pl-6">
							<PlaintextEditor color={error ? "red" : "base"} provider={$provider} value={src} on:input={update} on:change={handleChange} aria-labelledby="{id}-label" />
							{#if error}
								<Helper color="red" class="text-red-700">{sentenceCase(error)}</Helper>
							{/if}
						</div>
					</div>
				</div>
				<div class="flex flex-col gap-2">
					<P class="text-xs font-semibold tracking-wide text-gray-500 uppercase">Autoplay</P>
					<div class="flex flex-col gap-2">
						<div class="flex flex-col border-l-[5px] border-l-gray-300 pl-6">
							<Toggle size="small" checked={autoPlay} on:change={toggleAutoplay} />
						</div>
					</div>
				</div>
			</div>
			<P size="sm" color="text-gray-500" class="mt-2">{supportedProvidersString} videos are supported.</P>
			{#if src?.includes("synthesia.io")}
				<Alert class="mt-2">
					<span class="font-medium">Please note:</span>
					Synthesia videos do not support post-video forms or events.
				</Alert>
			{/if}
			{#if src?.includes("kaltura.com") && kalturaEnabled}
				<Alert class="mt-2">
					<span class="font-medium">Please note:</span>
					Kaltura videos do not support post-video forms or events.
				</Alert>
			{/if}
		</div>
		{#snippet fallback()}
			<div class={twMerge("flex flex-col gap-2 px-6", className)}>
				{@render children()}
			</div>
		{/snippet}
	</FeatureFlagGate>
</EditorInset>
